'use client';

import { useCallback, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useClerk, useSignIn } from '@clerk/nextjs';
import { match, P } from 'ts-pattern';

import {
  ChooseStrategy,
  ClientTrust,
  EmailCodeVerification,
  EmailStep,
  PasswordVerification,
} from '@/components/auth/sign-in';
import type { SignInStrategy } from '@/components/auth/type';
import { Button } from '@/components/ui/button';

export const SignIn = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectUrl = searchParams.get('redirect_url');
  const { client } = useClerk();
  const { signIn } = useSignIn();

  const lastStrategy = client?.lastAuthenticationStrategy as SignInStrategy | null;

  const [strategy, setStrategy] = useState<SignInStrategy>(lastStrategy ?? 'password');
  const [isChoosingStrategy, setIsChoosingStrategy] = useState(false);

  const handleChooseStrategy = useCallback((strategy?: SignInStrategy) => {
    if (strategy) {
      setStrategy(strategy);
      setIsChoosingStrategy(false);
    } else {
      setIsChoosingStrategy(true);
    }
  }, []);

  const handleRestart = useCallback(async () => {
    await signIn.reset();
  }, [signIn]);

  const handleComplete = useCallback(async () => {
    await signIn.finalize({
      navigate: ({ session, decorateUrl }) => {
        // Handle session tasks
        // See https://clerk.com/docs/guides/development/custom-flows/authentication/session-tasks
        if (session?.currentTask) {
          console.log(session?.currentTask);
          return;
        }

        // If no session tasks, navigate the signed-in user to the home page
        const url = decorateUrl(redirectUrl ?? '/');
        if (url.startsWith('http')) {
          window.location.href = url;
        } else {
          router.push(url);
        }
      },
    });
  }, [signIn, redirectUrl, router]);

  return (
    <div className="grid w-full grow items-center space-y-2 px-4 sm:justify-center">
      {match([signIn.status, strategy, isChoosingStrategy])
        .with(['needs_identifier', P._, false], () => <EmailStep />)
        .with(['needs_first_factor', 'password', false], () => (
          <>
            <PasswordVerification onChangeStrategy={handleChooseStrategy} onSubmit={handleComplete} />
            <Button type="button" size="sm" variant="link" onClick={handleRestart}>
              Sign in with a different account
            </Button>
          </>
        ))
        .with(['needs_first_factor', 'email_code', false], () => (
          <>
            <EmailCodeVerification onChangeStrategy={handleChooseStrategy} onSubmit={handleComplete} />
            <Button type="button" size="sm" variant="link" onClick={handleRestart}>
              Sign in with a different account
            </Button>
          </>
        ))
        .with(['needs_client_trust', 'email_code', false], () => (
          <>
            <ClientTrust onSubmit={handleComplete} />
            <Button type="button" size="sm" variant="link" onClick={handleRestart}>
              Sign in with a different account
            </Button>
          </>
        ))
        .with([P._, P._, true], () => <ChooseStrategy strategy={strategy} onSubmit={handleChooseStrategy} />)
        .otherwise(() => null)}
    </div>
  );
};
